import React, { useMemo, useState } from "react";
import { MdSchedule } from "react-icons/md";
import { useTranslation } from "react-i18next";
import i18n from "../../../../../i18n";
import NetworkTab from "./Components/Network/NetworkTab";
import AccountsTab from "./Components/Accounts/AccountsTab";
import useCarrierRows from "./Components/useCarrierRows";
import { carrierName } from "./carrierHelpers";
import { upcomingGroups } from "./Components/ComingSoon/upcomingCarriers";
import { connectableCarriers } from "./Components/Accounts/connectForms";

const NETWORK_TAB = 0;
const ACCOUNTS_TAB = 1;

const NETWORK_ROWS = {
  type: 1,
  subjectOf: carrierName,
  enabledText: (subject, next, count) =>
    i18n.t(
      next ? "settings:carriers.addedToQuotes" : "settings:carriers.hiddenFromQuotes",
      { subject, count },
    ),
};

const ACCOUNT_ROWS = {
  type: 0,
  subjectOf: (row) => row.account_name ?? carrierName(row),
  enabledText: (subject, next, count) =>
    i18n.t(next ? "settings:carriers.activated" : "settings:carriers.paused", {
      subject,
      count,
    }),
};

const StatTile = ({ value, label, dot = false, icon: Icon, highlight = false }) => (
  <div
    className={`min-w-[120px] rounded-[10px] border border-ui-border px-4 py-3 ${
      highlight ? "bg-brand-light" : "bg-ui-surface"
    }`}
  >
    <div className="flex items-center gap-1.5">
      {dot && <span className="h-2 w-2 rounded-full bg-state-success-dot" />}
      {Icon && <Icon className="h-[18px] w-[18px] text-brand" />}
      <span
        className={`text-2xl font-semibold leading-tight ${highlight ? "text-brand" : "text-ui-text"}`}
      >
        {value}
      </span>
    </div>
    <div className={`mt-0.5 text-xs ${highlight ? "text-brand" : "text-ui-text-muted"}`}>
      {label}
    </div>
  </div>
);

const StripStat = ({ value, label, dot = false, icon: Icon, highlight = false }) => (
  <div className="min-w-0 flex-1 px-[12px] py-2">
    <div className="flex items-center gap-1.5">
      {dot && <span className="h-2 w-2 rounded-full bg-state-success-dot" />}
      {Icon && <Icon className="h-4 w-4 text-brand" />}
      <span
        className={`text-base font-semibold leading-tight ${highlight ? "text-brand" : "text-ui-text"}`}
      >
        {value}
      </span>
    </div>
    <div className={`mt-0.5 text-[11px] ${highlight ? "text-brand" : "text-ui-text-muted"}`}>
      {label}
    </div>
  </div>
);

const TabButton = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`relative flex items-center gap-1.5 whitespace-nowrap px-3 py-2.5 text-sm font-medium transition-colors ${
      active ? "text-brand" : "text-ui-text-muted hover:text-brand"
    }`}
  >
    {children}
    {active && <span className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full bg-brand" />}
  </button>
);

const Carriers = () => {
  const { t } = useTranslation("settings");
  const [tab, setTab] = useState(NETWORK_TAB);
  const network = useCarrierRows(NETWORK_ROWS);
  const accounts = useCarrierRows(ACCOUNT_ROWS);
  const upcoming = useMemo(
    () =>
      network.loading
        ? []
        : upcomingGroups([
            ...network.rows.map(carrierName),
            ...connectableCarriers().map((carrier) => carrier.name),
          ]),
    [network.loading, network.rows],
  );
  const upcomingCount = upcoming.reduce((sum, group) => sum + group.items.length, 0);
  const liveValue = network.loading ? "–" : network.rows.length;
  const upcomingValue = network.loading ? "–" : upcomingCount;

  return (
    <div className="flex min-h-full flex-col bg-ui-bg">
      <section className="border-b border-ui-border bg-ui-surface">
        <div className="mx-auto max-w-[1400px] px-[16px] pt-[16px] sm:px-6 sm:pt-7">
          <div className="flex flex-wrap items-end justify-between gap-[14px] sm:gap-6">
            <div className="min-w-0 flex-[1_1_460px]">
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-brand">
                {t("carriers.eyebrow")}
              </div>
              <h1 className="mb-0 mt-2 font-jakarta text-[20px] font-bold leading-tight tracking-tight text-ui-text sm:mb-2 sm:text-[28px]">
                {t("carriers.headline")}
              </h1>
              <p className="mb-0 mt-2.5 hidden max-w-[640px] text-sm leading-relaxed text-ui-text-muted sm:mb-4 sm:block">
                {t("carriers.intro")}
              </p>
            </div>
            <div className="flex w-full divide-x divide-ui-border rounded-[10px] border-[1px] border-ui-border bg-ui-surface sm:hidden">
              <StripStat dot value={liveValue} label={t("carriers.stats.live")} />
              <StripStat highlight icon={MdSchedule} value={upcomingValue} label={t("carriers.stats.comingSoon")} />
            </div>
            <div className="hidden flex-wrap gap-2.5 sm:flex">
              <StatTile dot value={liveValue} label={t("carriers.stats.live")} />
              <StatTile highlight icon={MdSchedule} value={upcomingValue} label={t("carriers.stats.comingSoon")} />
              <StatTile value="CA + US" label={t("carriers.stats.modes")} />
            </div>
          </div>
          <nav className="scrollbar-hide mt-2.5 flex gap-1 overflow-x-auto sm:mt-[22px]">
            <TabButton active={tab === NETWORK_TAB} onClick={() => setTab(NETWORK_TAB)}>
              {t("carriers.tabs.network")}
            </TabButton>
            <TabButton active={tab === ACCOUNTS_TAB} onClick={() => setTab(ACCOUNTS_TAB)}>
              {t("carriers.tabs.accounts")}
              {!accounts.loading && (
                <span
                  className={`inline-flex h-[18px] min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold ${
                    tab === ACCOUNTS_TAB
                      ? "bg-brand-light text-brand"
                      : "bg-state-neutral-bg text-state-neutral-text"
                  }`}
                >
                  {accounts.rows.length}
                </span>
              )}
            </TabButton>
          </nav>
        </div>
      </section>

      <main className="mx-auto flex w-full max-w-[1400px] flex-1 flex-col gap-[20px] px-[16px] pb-12 pt-[12px] sm:gap-7 sm:px-6 sm:pt-[48px]">
        {tab === NETWORK_TAB ? (
          <NetworkTab {...network} upcoming={upcoming} />
        ) : (
          <AccountsTab {...accounts} upcoming={upcoming} />
        )}
      </main>
    </div>
  );
};

export default Carriers;
